import type { NestedFilterKeys } from "@dresscode-frontend/utility-types";
import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/table-core";
import type { ComponentProps } from "react";

import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import type { HelperOptions } from "@/components/DataTable/types";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { AvatarApp } from "@/features/app/components/Avatar/AvatarApp";

type App = ComponentProps<typeof AvatarApp> & {
  name: string;
  websiteUrl: string;
  avatarUrl?: string;
};

type Options = HelperOptions & {
  linkable?: boolean;
};

export function traceableAppColumn<TData extends { id: string }, V extends App>(
  id: NestedFilterKeys<TData, V>,
  options: Options,
): ColumnDef<TData> {
  const {
    i18nKey,
    minWidth = "160px",
    sortable = false,
    linkable = false,
  } = options;
  return {
    accessorKey: id,
    meta: { i18nKey, thStyle: { minWidth } },
    enableSorting: sortable,
    header: DataTableColumnHeader,
    cell: ({ row, getValue }) => {
      const app = getValue<App>();
      const shadowItAppId = row.original.id;

      return (
        <HStack className="gap-2 items-center">
          <AvatarApp src={app.avatarUrl} name={app.name} />
          {linkable ? (
            <Link
              to={"/apps/alerts/shadow-it/visit-histories"}
              search={{ shadowItAppIdIn: [shadowItAppId] }}
              className="gap-1 flex items-center hover:underline"
            >
              <Text variant="label">{app.name}</Text>
            </Link>
          ) : (
            <Text variant="label">{app.name}</Text>
          )}
        </HStack>
      );
    },
  };
}
